import { useRef, useState } from 'react';
import { StyleSheet, View, Modal as RNModal } from 'react-native';
import FlashMessage, { showMessage } from 'react-native-flash-message';
import { Button, Text, theme, styles as baseStyles } from '@socrait/react';

import { FlashBanner } from '~/components/FlashBanner';
import { LayoutWithSidebar } from '~/layouts/LayoutWithSidebar';
import { ClassAddEditScreen } from '~/screens/classAddEdit';
import { Period } from '~/services/types';
import { useUserProfile } from '~/services/useUserProfileQuery';

import { ClassListItem } from './classListItem';

type MultiClassEditScreenProps = {
  periods: Period[];
  onClose: () => void;
};

export function MultiClassEditScreen({ periods, onClose }: MultiClassEditScreenProps) {
  const [isAddEditOpen, setIsAddEditOpen] = useState<boolean>(false);
  const [activePeriodId, setActivePeriodId] = useState<string | null>(null);
  const notificationRef = useRef<FlashMessage>(null);
  const [operation, setOperation] = useState<'edit' | 'add'>('edit');
  const { user } = useUserProfile();

  const handlePeriodSelect = (period: Period) => {
    setActivePeriodId(period.id);
    setOperation('edit');
    setIsAddEditOpen(true);
  };

  const openAddClassModal = () => {
    setOperation('add');
    setActivePeriodId(null);
    setIsAddEditOpen(true);
  };

  const handleCloseAddEdit = (flashMessage?: string, type?: string) => {
    setIsAddEditOpen(false);

    if (!!flashMessage && !!type) {
      if (notificationRef?.current) {
        notificationRef.current.showMessage({ message: flashMessage, type: type as 'success' | 'warning' });
      } else {
        showMessage({ message: flashMessage, type: type as 'success' | 'warning' });
      }
    }
  };

  const existingShortNames = periods.map((period) => period.shortName).filter((shortName) => !!shortName);
  const classLimitReached = user?.isSpark ? periods.length === 3 : periods.length === 25;

  return (
    <LayoutWithSidebar isSimple={true} activePeriodId={activePeriodId || ''}>
      <View style={styles.container}>
        <Text style={styles.header}>Edit Classes</Text>
        <View style={styles.addClassContainer}>
          <Button
            name="Add New Class"
            variant="primary"
            icon="plus"
            iconColor={theme.colors.white}
            onPress={openAddClassModal}
            disabled={classLimitReached}
            size="large"
          />
          {classLimitReached && (
            <Text style={styles.classLimitText}>
              Maximum number of classes reached. You cannot add any more classes.
            </Text>
          )}
        </View>
        <View style={styles.classListContainer}>
          {periods.map((period, i) => (
            <ClassListItem key={i} period={period} onPeriodSelect={handlePeriodSelect} />
          ))}
        </View>
        <View style={styles.doneContainer}>
          <Button name="Done" variant="secondary" fontStyle={styles.doneText} onPress={onClose} />
        </View>
      </View>
      <RNModal visible={isAddEditOpen} onRequestClose={() => setIsAddEditOpen(false)} animationType="none">
        <ClassAddEditScreen
          periodId={activePeriodId || ''}
          operation={operation}
          existingShortNames={existingShortNames}
          onClose={handleCloseAddEdit}
          onSubmit={() => setIsAddEditOpen(false)}
          numPeriods={periods.length}
        />
      </RNModal>
      <FlashMessage
        ref={notificationRef}
        position="top"
        animationDuration={100}
        duration={10000}
        MessageComponent={({ message }: { message: { message: string; type: 'warning' | 'success' } }) => (
          <FlashBanner message={message} />
        )}
      />
    </LayoutWithSidebar>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: theme.spacing.xlarge * 2,
    padding: theme.spacing.xlarge * 2,
    position: 'relative',
  },
  header: {
    ...baseStyles.boldText,
    ...baseStyles.largeText,
    flexShrink: 0,
  },
  addClassContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xlarge,
  },
  classLimitText: {
    fontSize: theme.fontSizes.small,
    color: theme.colors.darkGray,
  },
  classListContainer: {
    flexShrink: 1,
    gap: theme.spacing.medium,
    height: '100%',
    overflowY: 'auto',
    width: '100%',
  },
  doneContainer: {
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  doneText: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
  },
});
